import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../lib/auth'
import { supabase } from '../lib/supabase'
import { db, type Conversation } from '../lib/db'
import { asJson } from '../lib/db-types'

const BATCH_SIZE = 200

interface BasicContactRow {
  profile_urn: string
  name: string
  headline: string | null
  profile_image_url: string | null
  last_interaction_at: string | null
}

/**
 * Extract basic (non-enriched) contact rows from conversations.
 * Deduplicates by profileUrn, keeping the latest interaction.
 */
function extractBasicContacts(conversations: Conversation[]): BasicContactRow[] {
  const byUrn = new Map<string, BasicContactRow>()

  for (const conv of conversations) {
    for (const p of (conv.allParticipants || [])) {
      if (!p.profileUrn) continue
      const interactionTs = conv.lastMessageTimestamp
        ? new Date(conv.lastMessageTimestamp).toISOString()
        : null
      const existing = byUrn.get(p.profileUrn)
      if (existing) {
        if ((interactionTs || '') > (existing.last_interaction_at || '')) {
          existing.name = p.name || existing.name
          existing.headline = p.headline || existing.headline
          existing.profile_image_url = p.profileImage ?? existing.profile_image_url
          existing.last_interaction_at = interactionTs
        }
      } else {
        byUrn.set(p.profileUrn, {
          profile_urn: p.profileUrn,
          name: p.name || 'Unknown',
          headline: p.headline || null,
          profile_image_url: p.profileImage ?? null,
          last_interaction_at: interactionTs,
        })
      }
    }
  }

  return Array.from(byUrn.values())
}

async function upsertBasicContacts(rows: BasicContactRow[]): Promise<number> {
  if (rows.length === 0) return 0
  let synced = 0
  for (let i = 0; i < rows.length; i += BATCH_SIZE) {
    const batch = rows.slice(i, i + BATCH_SIZE)
    const { error } = await supabase.rpc('upsert_contacts', { payload: asJson(batch) })
    if (error) {
      console.error('[Slash] Basic contact sync batch failed:', error.message)
    } else {
      synced += batch.length
    }
  }
  return synced
}

/**
 * Syncs basic contact data (name/headline/image) from IDB to Supabase.
 *
 * Full sync on mount, incremental sync on IDB updates.
 * Rich enrichment is triggered on-demand from ThreadView when the user
 * opens a conversation — NOT in bulk here.
 *
 * Mount once in Shell.tsx. No render output — pure side-effect hook.
 */
export function useContactSync() {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const didFullSync = useRef<string | null>(null) // tracks which userId completed full sync
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const basicFullSync = useCallback(async () => {
    if (!userId) return
    try {
      const conversations = await db.conversations.toArray()
      const contacts = extractBasicContacts(conversations)
      const count = await upsertBasicContacts(contacts)
      if (count > 0) console.log(`[Slash] Contact sync: ${count} contacts upserted`)
    } catch (err) {
      console.warn('[Slash] Contact full sync failed:', (err as Error).message)
    }
  }, [userId])

  const basicIncrementalSync = useCallback(async (conversationUrns: string[]) => {
    if (!userId || conversationUrns.length === 0) return
    try {
      const conversations = await db.conversations
        .where('conversationUrn')
        .anyOf(conversationUrns)
        .toArray()
      const contacts = extractBasicContacts(conversations)
      await upsertBasicContacts(contacts)
    } catch (err) {
      console.warn('[Slash] Contact incremental sync failed:', (err as Error).message)
    }
  }, [userId])

  // Full sync once on mount (3s delay to let IDB populate first)
  useEffect(() => {
    if (!userId || didFullSync.current === userId) return
    const timer = setTimeout(() => {
      didFullSync.current = userId
      basicFullSync()
    }, 3000)
    return () => clearTimeout(timer)
  }, [userId, basicFullSync])

  // Listen for IDB updates — incremental basic sync only
  useEffect(() => {
    if (!userId) return
    const pendingUrns = new Set<string>()

    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail?.type !== 'conversations') return
      const urns = detail.conversationUrns as string[] | undefined
      if (!urns) return

      urns.forEach((u) => pendingUrns.add(u))

      if (pendingTimer.current) clearTimeout(pendingTimer.current)
      pendingTimer.current = setTimeout(() => {
        const batch = Array.from(pendingUrns)
        pendingUrns.clear()
        basicIncrementalSync(batch)
      }, 5000)
    }

    window.addEventListener('fwdslash-data-updated', handler)
    return () => {
      window.removeEventListener('fwdslash-data-updated', handler)
      if (pendingTimer.current) clearTimeout(pendingTimer.current)
    }
  }, [userId, basicIncrementalSync])

  // Listen for batch enrichment results from the test/background enricher.
  // These arrive via window.postMessage from content-data.js and contain
  // full enrichment data that needs to be written to Supabase.
  useEffect(() => {
    if (!userId) return

    const enrichmentQueue: Array<Record<string, unknown>> = []
    let flushTimer: ReturnType<typeof setTimeout> | null = null
    let flushInFlight = false

    // Flush a bounded snapshot of the queue. Key property: the items aren't
    // removed from the queue until the RPC succeeds. If the RPC throws or
    // returns an error, they stay queued and the next flush retries — a
    // network blip or brief outage no longer silently drops enrichments.
    const flush = async () => {
      if (flushInFlight || enrichmentQueue.length === 0) return
      flushInFlight = true
      const batchSize = Math.min(enrichmentQueue.length, 10)
      // Snapshot (don't splice) — leave the rows in place so a failed RPC
      // rolls back to "still queued".
      const batch = enrichmentQueue.slice(0, batchSize)
      try {
        const { error } = await supabase.rpc('upsert_contacts', { payload: asJson(batch) })
        if (error) {
          console.error(`[Slash] Batch enrichment write failed (${batch.length}):`, error.message)
          // Leave rows queued — next flush retries. A persistent failure
          // will log once per flush interval until the queue is drained
          // or the user navigates away (and the effect-cleanup final
          // best-effort flush fires).
          return
        }
        // Only now remove the sent rows. If the queue grew during the
        // RPC, those newer rows stay in place and flush on the next tick.
        enrichmentQueue.splice(0, batch.length)
        console.log(`[Slash] Batch enrichment: ${batch.length} contacts written to Supabase`)
      } catch (err) {
        console.error(`[Slash] Batch enrichment threw (${batch.length}):`, (err as Error).message)
        // Same as above — leave rows queued.
      } finally {
        flushInFlight = false
        // If more items arrived during the RPC, schedule another flush.
        if (enrichmentQueue.length > 0 && !flushTimer) {
          flushTimer = setTimeout(() => { flushTimer = null; void flush() }, 5000)
        }
      }
    }

    const handler = (event: MessageEvent) => {
      // Only trust messages from our own window on our own origin. These
      // enrichments go straight into `upsert_contacts` — letting an iframe
      // or third-party script spoof them would let them plant arbitrary
      // rows in the user's contacts table.
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'FWDSLASH_BATCH_ENRICHMENT') return
      const { profileUrn, name, enrichment } = event.data
      if (!profileUrn || !enrichment) return
      console.log(`[Slash] Enrichment received: ${name} — queueing for Supabase write`)

      const now = new Date().toISOString()
      // Only send name if we have a real one from the profile — don't overwrite
      // the existing name with URN fallbacks from the batch enricher.
      const derivedName = enrichment.firstName
        ? `${enrichment.firstName || ''} ${enrichment.lastName || ''}`.trim()
        : null
      enrichmentQueue.push({
        profile_urn: profileUrn,
        name: derivedName,
        first_name: enrichment.firstName,
        last_name: enrichment.lastName,
        headline: null, // not available in batch context
        profile_image_url: null,
        location: enrichment.location,
        industry: enrichment.industry,
        public_identifier: enrichment.publicIdentifier,
        current_title: enrichment.currentTitle,
        current_company: enrichment.currentCompany,
        premium: enrichment.premium ?? null,
        influencer: enrichment.influencer ?? null,
        creator: enrichment.creator ?? null,
        verified: enrichment.verified ?? null,
        connected_at: enrichment.connectedAt,
        experience: enrichment.experience || [],
        education: enrichment.education || [],
        skills: enrichment.skills || [],
        enriched_at: now,
      })

      // Flush every 10 contacts or 5 seconds after the first queued item,
      // whichever comes first. The timer is set once when the queue goes
      // from empty to non-empty and NOT reset on subsequent arrivals —
      // otherwise a sustained 1/sec stream would never flush.
      if (enrichmentQueue.length >= 10) {
        if (flushTimer) { clearTimeout(flushTimer); flushTimer = null }
        void flush()
      } else if (!flushTimer) {
        flushTimer = setTimeout(() => { flushTimer = null; void flush() }, 5000)
      }
    }

    window.addEventListener('message', handler)
    return () => {
      window.removeEventListener('message', handler)
      if (flushTimer) clearTimeout(flushTimer)
      // Best-effort final flush on unmount — but ONLY if the current auth
      // session still matches the userId this effect was set up for. The
      // effect cleanup typically fires because `userId` changed (sign-out,
      // or sign-in-as-different-user); in that case the enrichments in
      // our queue were collected under the *previous* session. Writing
      // them now would commit them under whoever `auth.uid()` resolves to
      // at flush time, silently leaking one user's enrichment queue into
      // another user's contacts table. Skip the flush instead — the
      // enrichments will re-arrive naturally next session.
      if (enrichmentQueue.length === 0 || flushInFlight) return
      const capturedUserId = userId
      const pending = enrichmentQueue.slice()
      void supabase.auth.getSession().then(({ data }) => {
        if (data.session?.user.id !== capturedUserId) return
        return supabase.rpc('upsert_contacts', { payload: asJson(pending) })
      })
    }
  }, [userId])

  // Listen for connections enumeration pages — write as basic contacts.
  // These appear via FWDSLASH_CONNECTIONS_PAGE from the ENUMERATE_ALL_CONNECTIONS command.
  useEffect(() => {
    if (!userId) return

    const handler = async (event: MessageEvent) => {
      // Same same-window/same-origin guard as the enrichment handler above —
      // these rows also feed `upsert_contacts`.
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'FWDSLASH_CONNECTIONS_PAGE') return
      const connections = event.data.connections as Array<{
        name: string
        headline: string
        profileUrn: string
        profileImage: string | null
      }> | undefined
      if (!connections || connections.length === 0) return

      const rows = connections
        .filter((c) => c.profileUrn)
        .map((c) => ({
          profile_urn: c.profileUrn.startsWith('urn:li:') ? c.profileUrn : `urn:li:fsd_profile:${c.profileUrn}`,
          name: c.name || 'Unknown',
          headline: c.headline || null,
          profile_image_url: c.profileImage || null,
        }))

      if (rows.length === 0) return
      const { error } = await supabase.rpc('upsert_contacts', { payload: asJson(rows) })
      if (error) {
        console.error(`[Slash] Connections page write failed (${rows.length}):`, error.message)
      } else {
        console.log(`[Slash] Connections page: ${rows.length} contacts written to Supabase`)
      }
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [userId])
}
